import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.types";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_CONTAINER_COLUMN, JUSTIFY_CONTENT, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { ButtonStyle, CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CheckboxMode, CjsCheckbox } from "@cervval.js/client-html5-ui/src/components/checkbox/checkbox";
import { I18nAcceptTerm } from "./acceptTerms.i18n";

export interface AcceptTermsOptions {
    /** Terms to display as an HTML string */
    terms: string;
    /** Callback called when accepting the terms */
    acceptCallback: () => void;
}

const defaultAcceptTermsOptions: AcceptTermsOptions = {
    terms: "No terms to display",
    acceptCallback: () => { }
};

@CustomElement({
    name: "accept-terms",
    template: require("./acceptTerms.html"),
    style: require("./acceptTerms.scss"),
    i18n: require("./acceptTerms.i18n").i18nAcceptTerm,
    elementCssClasses: [W_100, FLEX_CONTAINER_COLUMN, JUSTIFY_CONTENT.BETWEEN]
})
export class AcceptTerms<I18n extends I18nAcceptTerm = I18nAcceptTerm> extends WebComponent<I18n> {

    @TypedWebComponentOptions(defaultAcceptTermsOptions)
    public readonly options!: WebComponentOptions<AcceptTermsOptions>;


    constructor(options?: Partial<AcceptTermsOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    @TypedHtmlElements({ ensureLoadedClasses: [CjsCheckbox, CjsButton] })
    public readonly components!: {
        termsContainer: HTMLDivElement,
        consentCheckbox: CjsCheckbox,
        acceptButton: CjsButton
    };

    /** @override */
    public override init(): void {

        this.components.consentCheckbox.options.setAndRefreshIfChanged({
            mode: CheckboxMode.CHECKBOX,
            color: "#045E53",
            enableIndeterminate: false,
            enableLabelClick: true,
            label: this.i18n.acceptPrompt
        });

        this.components.acceptButton.options.setAndRefreshIfChanged({
            cssStyle: ButtonStyle.PRIMARY,
            label: this.i18n.confirmConsent,
            disabled: true
        });

        this.components.consentCheckbox.toggleEvent.on(() => {
            const checkboxValue = this.components.consentCheckbox.getValue();
            this.components.acceptButton.options.setAndRefreshIfChanged({
                disabled: checkboxValue == null || !checkboxValue ? true : false
            });
        });

        this.components.acceptButton.clickEvent.on(() => {
            const checkboxValue = this.components.consentCheckbox.getValue();
            if (checkboxValue == null || !checkboxValue) { return; }

            this.options.acceptCallback();
        });
    }

    /** @override */
    public override refresh(): void {
        this.components.termsContainer.innerHTML = this.options.terms;

        this.components.consentCheckbox.setValue(false);
        this.components.consentCheckbox.options.setAndRefreshIfChanged({ disabled: false });
        this.components.acceptButton.options.setAndRefreshIfChanged({ disabled: true });
    }

    /** Disable interactive elements of the component : the checkbox to give consent and the accept button */
    public disableElements(): void {
        this.components.acceptButton.options.setAndRefreshIfChanged({ disabled: true });
        this.components.consentCheckbox.options.setAndRefreshIfChanged({ disabled: true });
    }
}